import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ArrowRight } from 'lucide-react';
import { getExpertiseTags, getAllProfiles } from '../../services/profileService';
import { fuzzyFilterStrings } from '../../utils/fuzzy';
import {
  expertiseKpis, coveragePercent, sortSkills, categoryCoverage, SKILL_SORTS, THIN_THRESHOLD,
} from '../../utils/expertiseMetrics';
import { getEmployeeSkillSet } from '../../hooks/useSearch';
import { schemeForEmployee } from '../../utils/deptColors';
import { PageHeader } from '../ui/PageHeader';

/**
 * In-place people list for a skill — opens over the Expertise page so
 * browsing skills never navigates away. Clicking a person opens their
 * profile modal on top; closing it returns here.
 */
function SkillPeopleModal({ skill, people, photos, isDark, onSelectEmployee, onBrowsePeople, onClose }) {
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 14 }}
          transition={{ type: 'spring', damping: 26, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`People with ${skill}`}
          className="w-full max-w-md max-h-[70vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl"
          style={{
            background: isDark ? 'rgba(17,18,23,0.98)' : 'rgba(248,250,252,0.98)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(148,163,184,0.18)'}`,
          }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-5 py-4 flex-shrink-0"
            style={{
              background: isDark ? 'rgba(94,234,212,0.07)' : 'rgba(15,118,110,0.05)',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(148,163,184,0.14)'}`,
            }}
          >
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-bold truncate" style={{ color: isDark ? '#f4f4f5' : '#0f172a' }}>
                {skill}
              </h3>
              <p className="text-[11px] mt-0.5" style={{ color: isDark ? '#5eead4' : '#0f766e' }}>
                {people.length} {people.length === 1 ? 'person' : 'people'} with this skill
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-full transition-colors flex-shrink-0"
              style={{ color: isDark ? '#71717a' : '#94a3b8' }}
            >
              <X size={14} />
            </button>
          </div>

          {/* People list */}
          <div className="overflow-y-auto flex-1 p-2.5 space-y-1.5">
            {people.map((employee) => {
              const scheme = schemeForEmployee(employee);
              const photo = photos?.[employee.id] || employee.openAssetPhoto;
              return (
                <button
                  key={employee.id}
                  onClick={() => onSelectEmployee?.(employee)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors"
                  style={{
                    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(148,163,184,0.15)'}`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)'; }}
                >
                  {photo ? (
                    <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
                  ) : (
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white"
                      style={{ background: scheme.accent }}
                    >
                      {(employee.displayName || '?').split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate" style={{ color: isDark ? '#f4f4f5' : '#0f172a' }}>
                      {employee.displayName}
                    </p>
                    <p className="text-[11px] truncate mt-0.5" style={{ color: isDark ? '#a1a1aa' : '#64748b' }}>
                      {employee.jobTitle || 'Employee'}
                    </p>
                  </div>
                  {employee.department && (
                    <span
                      className="text-[9px] font-semibold rounded-full px-2 py-[3px] flex-shrink-0 truncate max-w-[110px]"
                      style={{
                        background: isDark ? scheme.badge : `${scheme.accent}14`,
                        color: isDark ? scheme.badgeText : scheme.textLight,
                      }}
                    >
                      {employee.department}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer — secondary path into the filtered directory */}
          {onBrowsePeople && (
            <div
              className="px-4 py-3 flex-shrink-0"
              style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(148,163,184,0.14)'}` }}
            >
              <button
                onClick={() => onBrowsePeople(skill)}
                className="w-full h-9 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
                style={{
                  color: isDark ? '#5eead4' : '#0f766e',
                  border: `1px solid ${isDark ? 'rgba(94,234,212,0.25)' : 'rgba(15,118,110,0.2)'}`,
                }}
              >
                Filter the directory by this skill <ArrowRight size={12} />
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}


/** Total tags in the taxonomy, for the header count. */
function taxonomyTagTotal(categories) {
  return categories.reduce((sum, category) => sum + category.tags.length, 0);
}

/** .kpi — mono display number, uppercase label, one line of context. */
function Kpi({ label, value, detail, tone }) {
  const toneClass =
    tone === 'on' ? 'border-accent-line bg-accent-soft'
    : tone === 'risk' ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30'
    : 'border-line bg-surface';
  const valueClass =
    tone === 'on' ? 'text-accent'
    : tone === 'risk' ? 'text-amber-800 dark:text-amber-300'
    : 'text-ink';
  return (
    <div className={`rounded-card border p-3 shadow-e1 ${toneClass}`}>
      <p className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-ink-3">{label}</p>
      <p className={`font-mono text-[21px] font-bold tabular-nums leading-none mt-1 ${valueClass}`}>{value}</p>
      <p className="text-[11.5px] text-ink-3 mt-[3px] leading-[1.4]">{detail}</p>
    </div>
  );
}

/**
 * .skcard — name, holder count, a coverage bar scaled against the deepest skill,
 * and a face stack. A 1–2 person skill gets a risk pill instead of looking
 * identical to a 96-person one, which is the whole point of the view.
 */
function SkillCard({ entry, maxCount, onSelect }) {
  const { skill, count, people = [], officeCount = 0 } = entry;
  const zero = !count;
  const thin = count > 0 && count <= THIN_THRESHOLD;
  const width = coveragePercent(count, maxCount);

  return (
    <button
      onClick={() => !zero && onSelect(skill)}
      disabled={zero}
      title={zero ? `Nobody has claimed ${skill} yet` : `See ${count} ${count === 1 ? 'person' : 'people'} with ${skill}`}
      className={`text-left rounded-card border p-3 flex flex-col gap-2.5 min-w-0 transition-all
        ${zero
          ? 'border-dashed border-line bg-surface opacity-75 cursor-default'
          : 'border-line bg-surface shadow-e1 hover:shadow-e2 hover:border-accent-line hover:-translate-y-[1px] cursor-pointer'}`}
    >
      <span className="flex items-start gap-2">
        <span className="flex-1 min-w-0 text-[13.5px] font-bold tracking-[-0.01em] text-ink leading-[1.3] truncate">
          {skill}
        </span>
        <span className={`font-mono text-[12px] font-bold flex-none ${zero ? 'text-ink-4' : 'text-accent'}`}>
          {count}
        </span>
      </span>

      <span className="block h-1 rounded-[3px] bg-surface-2 overflow-hidden">
        <span
          className={`block h-full rounded-[3px] ${thin ? 'bg-amber-500' : zero ? 'bg-ink-4' : 'bg-accent'}`}
          style={{ width: `${width}%` }}
        />
      </span>

      <span className="flex items-center gap-1.5 flex-wrap text-[11px] text-ink-3">
        {people.length > 0 && (
          <span className="flex pl-1">
            {people.slice(0, 5).map((person) => (
              <span
                key={person.id}
                title={person.displayName}
                className="-ml-2 w-7 h-7 rounded-full bg-surface-2 ring-2 ring-surface
                           inline-flex items-center justify-center text-[10.5px] font-bold text-ink-3"
              >
                {(person.displayName || '?').split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
              </span>
            ))}
          </span>
        )}
        {thin && (
          <span className="inline-flex items-center h-5 px-1.5 rounded font-mono text-[9.5px] font-bold uppercase
                           bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300">
            {count} deep
          </span>
        )}
        {officeCount > 0 && <span>{officeCount} {officeCount === 1 ? 'office' : 'offices'}</span>}
      </span>
    </button>
  );
}


/**
 * Expertise browser — answers "who is good at X?" by browsing the firm's
 * skill taxonomy. Each tag shows a live people count; clicking a tag opens
 * the people list in place (no navigation away from this page).
 */
export function ExpertiseView({ employees, photos, allSkills, isDark, onSelectEmployee, onBrowsePeople }) {
  const [query, setQuery] = useState('');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [pane, setPane] = useState('covered');
  const [activeCategory, setActiveCategory] = useState(null);
  const [sort, setSort] = useState(SKILL_SORTS.MOST);

  // tag -> people count across every skill source
  const countsByTag = useMemo(
    () => new Map(allSkills.map(({ skill, count }) => [skill, count])),
    [allSkills],
  );

  const categories = useMemo(() => {
    const taxonomy = getExpertiseTags();
    const q = query.trim();

    // Tags that people actually have but the taxonomy doesn't know about
    // (e.g. firm-curated OpenAsset skills) get their own bucket.
    const taxonomyTags = new Set(taxonomy.flatMap((c) => c.tags));
    const adHoc = allSkills
      .filter(({ skill }) => !taxonomyTags.has(skill))
      .map(({ skill }) => skill);

    return [...taxonomy, ...(adHoc.length ? [{ id: 'other', name: 'More Skills', tags: adHoc }] : [])]
      .map((category) => ({
        ...category,
        tags: fuzzyFilterStrings(category.tags, q)
          .map((tag) => ({ tag, count: countsByTag.get(tag) || 0 }))
          .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag)),
      }))
      .filter((category) => category.tags.length > 0);
  }, [query, countsByTag, allSkills]);

  const skillIndex = useMemo(
    () => new Map(allSkills.map((entry) => [entry.skill, entry])),
    [allSkills],
  );

  const kpis = useMemo(
    () => expertiseKpis(allSkills, employees, taxonomyTagTotal(categories)),
    [allSkills, employees, categories],
  );

  const coveredCategories = useMemo(() => categoryCoverage(
    categories.map((category) => ({ ...category, tags: category.tags.map((t) => t.tag) })),
    countsByTag,
  ), [categories, countsByTag]);

  const categoryCount = categories.length;
  const activeCategoryName = activeCategory
    ? (categories.find((c) => c.id === activeCategory)?.name ?? 'All')
    : (pane === 'gaps' ? 'All uncovered tags' : 'All covered skills');

  // Tags become full skill entries so a card can show holders and offices, not
  // just a number. A tag nobody has has no entry, hence the zero fallback.
  const visibleSkills = useMemo(() => {
    const source = activeCategory
      ? categories.filter((c) => c.id === activeCategory)
      : categories;
    const seen = new Set();
    const entries = [];
    for (const category of source) {
      for (const { tag, count } of category.tags) {
        if (seen.has(tag)) continue;
        seen.add(tag);
        entries.push(skillIndex.get(tag) || { skill: tag, count, people: [], officeCount: 0 });
      }
    }
    const filtered = pane === 'gaps'
      ? entries.filter((e) => !e.count)
      : entries.filter((e) => e.count > 0);
    return sortSkills(filtered, sort);
  }, [categories, activeCategory, pane, sort, skillIndex]);

  // People for the currently open skill, freshest read at open time
  const selectedSkillPeople = useMemo(() => {
    if (!selectedSkill) return [];
    const profiles = getAllProfiles();
    return employees
      .filter((e) => getEmployeeSkillSet(e, profiles[e.id] || {}).has(selectedSkill))
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  }, [selectedSkill, employees]);

  return (
    <div className="w-full">
      {/* ── Page header with the four numbers that matter ────────────────────
          Mockup 1A. This view used to open with a search box and two vanity
          stats. The uncomfortable numbers are the point: how much of the firm is
          untagged, and how many capabilities rest on exactly one person. */}
      <div className="mb-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <PageHeader title="Expertise">
            <b className="font-mono font-semibold text-ink-2">{kpis.totalTags}</b> tags ·{' '}
            <b className="font-mono font-semibold text-ink-2">{categoryCount}</b> categories ·{' '}
            <b className="font-mono font-semibold text-ink-2">{kpis.taggedPeopleCount}</b> of{' '}
            <b className="font-mono font-semibold text-ink-2">{employees.length}</b> people tagged
          </PageHeader>

          <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
            <div className="relative flex-1 min-w-0 max-w-[420px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-ink-4" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search skills, software, project types…"
                className="w-full h-[38px] rounded-ctl pl-9 pr-3 text-[13.5px] outline-none
                           bg-surface border border-line text-ink shadow-e1
                           focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)] transition-shadow"
                style={{ caretColor: 'var(--accent)' }}
              />
            </div>
            <div className="flex items-center rounded-ctl border border-line bg-surface p-0.5 shadow-e1 flex-none">
              {[
                { id: 'covered', label: 'Covered', count: kpis.coveredCount },
                { id: 'gaps', label: 'Gaps', count: kpis.uncoveredCount },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setPane(tab.id)}
                  aria-pressed={pane === tab.id}
                  className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-semibold transition-colors
                    ${pane === tab.id ? 'bg-surface-2 text-ink-2 shadow-e1' : 'text-ink-4 hover:text-ink-2'}`}
                >
                  {tab.label}
                  <span className="font-mono text-[10px] opacity-70">{tab.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* .kpis */}
        <div className="grid gap-2.5 mt-3.5 grid-cols-[repeat(auto-fit,minmax(148px,1fr))]">
          <Kpi label="Tagged people" value={kpis.taggedPeopleCount}
               detail={`${kpis.taggedPercent}% of the firm · ${kpis.untaggedPeopleCount} untagged`} />
          <Kpi label="Deepest capability" value={kpis.deepest?.count ?? 0} tone="on"
               detail={kpis.deepest ? `${kpis.deepest.skill} · ${kpis.deepest.officeCount || 0} offices` : 'No skills tagged yet'} />
          <Kpi label="Single point of failure" value={kpis.singlePointCount} tone="risk"
               detail="skills held by exactly 1 person" />
          <Kpi label="Uncovered capability" value={kpis.uncoveredCount}
               detail="tags nobody claims yet" />
        </div>
      </div>

      {/* .pane2 — sticky category rail + skill cards */}
      <div className="rounded-card border border-line bg-surface shadow-e1 overflow-hidden
                      grid md:grid-cols-[198px_1fr] min-w-0">
        <nav className="border-b md:border-b-0 md:border-r border-line bg-surface-2 p-2 pb-4 min-w-0">
          <p className="text-[9.5px] font-bold uppercase tracking-[0.13em] text-ink-4 px-2.5 pb-1.5">Categories</p>
          <button
            onClick={() => setActiveCategory(null)}
            {...(!activeCategory ? { 'aria-current': 'true' } : {})}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-[7px] text-[12.5px] font-semibold text-left transition-colors
              ${!activeCategory ? 'bg-surface text-accent shadow-e1' : 'text-ink-2 hover:bg-surface hover:text-ink'}`}
          >
            All {pane === 'gaps' ? 'gaps' : 'covered'}
            <span className={`ml-auto font-mono text-[10.5px] ${!activeCategory ? 'text-accent' : 'text-ink-4'}`}>
              {pane === 'gaps' ? kpis.uncoveredCount : kpis.coveredCount}
            </span>
          </button>
          <div className="h-px bg-line mx-2.5 my-2" />
          {coveredCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              {...(activeCategory === category.id ? { 'aria-current': 'true' } : {})}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-[7px] text-[12.5px] font-semibold text-left transition-colors
                ${activeCategory === category.id ? 'bg-surface text-accent shadow-e1' : 'text-ink-2 hover:bg-surface hover:text-ink'}`}
            >
              <span className="truncate">{category.name}</span>
              <span className={`ml-auto font-mono text-[10.5px] flex-none ${activeCategory === category.id ? 'text-accent' : 'text-ink-4'}`}>
                {category.coveredCount}/{category.totalCount}
              </span>
            </button>
          ))}
        </nav>

        <div className="min-w-0 p-3.5 pb-4 bg-surface">
          <div className="flex items-center gap-2.5 mb-3 flex-wrap">
            <span className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-ink-3">
              {activeCategoryName}
            </span>
            <span className="font-mono text-[10.5px] text-ink-4">
              {visibleSkills.length} {pane === 'gaps' ? 'uncovered' : 'covered'}
            </span>
            <span className="flex-1 h-px bg-line" />
            <div className="flex items-center rounded-ctl border border-line bg-surface p-0.5 flex-none">
              {[
                { id: SKILL_SORTS.MOST, label: 'Most people' },
                { id: SKILL_SORTS.THINNEST, label: 'Thinnest' },
                { id: SKILL_SORTS.ALPHA, label: 'A–Z' },
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => setSort(option.id)}
                  aria-pressed={sort === option.id}
                  className={`h-6 px-2 rounded-md text-[11px] font-semibold transition-colors
                    ${sort === option.id ? 'bg-surface-2 text-ink-2' : 'text-ink-4 hover:text-ink-2'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {visibleSkills.length === 0 ? (
            <p className="text-[12.5px] text-ink-3 py-8 text-center">
              {pane === 'gaps'
                ? 'Every tag in this category has someone behind it.'
                : 'Nothing tagged in this category yet.'}
            </p>
          ) : (
            <div className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(228px,1fr))]">
              {visibleSkills.map((entry) => (
                <SkillCard
                  key={entry.skill}
                  entry={entry}
                  maxCount={kpis.deepest?.count ?? 0}
                  onSelect={setSelectedSkill}
                />
              ))}
            </div>
          )}
        </div>
      </div>


      {selectedSkill && (
        <SkillPeopleModal
          key={selectedSkill}
          skill={selectedSkill}
          people={selectedSkillPeople}
          photos={photos}
          isDark={isDark}
          onSelectEmployee={onSelectEmployee}
          onBrowsePeople={onBrowsePeople}
          onClose={() => setSelectedSkill(null)}
        />
      )}
    </div>
  );
}
